'use client';

import { useState } from 'react';

import type { RichOrderItem } from '@ps/api/types';
import { currencyFormatter } from '@ps/functions/utils/currency-formatter';
import { Modal } from '@ps/ui/molecules/Modal';
import clsx from 'clsx';
import { Check } from 'lucide-react';

import {
  orderProductImage,
  orderProductName,
} from '../../../../orders/functions/orderRowDisplay';
import type { OrderMode } from '../../../../orders/types';
import { useShareableOrders } from './useShareableOrders';

interface Props {
  open: boolean;
  onClose: () => void;
  role: OrderMode;
  currentUserId: string;
  partnerId: string;
  onConfirm: (orders: RichOrderItem[]) => void;
}

/**
 * Order picker for the composer's "Share Order" action — a scrollable
 * multi-select grid of the orders shared between the two chat
 * participants. Confirming sends one order message per selection.
 * Mirrors PopshopRN's `ShareOrderPage` sheet.
 */
export function ShareOrderDialog({
  open,
  onClose,
  role,
  currentUserId,
  partnerId,
  onConfirm,
}: Props) {
  const { data, isInitializing, isLoadingMore, hasMore, loadMore } =
    useShareableOrders(role, currentUserId, partnerId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const selected = data.filter(order => selectedIds.has(order.id));
    if (selected.length === 0) return;
    onConfirm(selected);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      theme="dark"
      title="Share Order"
      className="flex max-h-[80vh] w-[min(560px,92vw)] flex-col gap-4"
    >
      <div className="min-h-0 flex-1 overflow-y-auto">
        {isInitializing ? (
          <p className="text-content-3 py-10 text-center text-sm">
            Loading orders…
          </p>
        ) : data.length === 0 ? (
          <p className="text-content-3 py-10 text-center text-sm">
            No orders to share.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {data.map(order => {
                const selected = selectedIds.has(order.id);
                const image = orderProductImage(order);
                return (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => toggle(order.id)}
                    className={clsx(
                      'relative flex flex-col gap-2 rounded-xl border p-2 text-left transition',
                      selected
                        ? 'border-brand bg-brand/10'
                        : 'border-line hover:border-line-strong',
                    )}
                  >
                    {selected ? (
                      <span className="bg-brand text-content-on-brand absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full">
                        <Check className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    ) : null}
                    {image ? (
                      // Plain <img> — remote firebase storage URLs aren't in
                      // the Next image config (same as the order bubble).
                      <img
                        src={image}
                        alt=""
                        className="bg-surface-1 aspect-[6/5] w-full rounded-lg object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="bg-surface-1 aspect-[6/5] w-full rounded-lg" />
                    )}
                    <div className="flex flex-col gap-0.5">
                      <span className="text-content-3 truncate text-[11px] font-semibold uppercase">
                        Order #{order.orderNumber}
                      </span>
                      <span className="text-content-1 truncate text-sm font-semibold">
                        {orderProductName(order)}
                      </span>
                      <span className="text-content-1 text-sm font-semibold">
                        {currencyFormatter.formatUSD(
                          order.totalPriceIncludeSlaveOrders,
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            {hasMore ? (
              <button
                type="button"
                onClick={loadMore}
                disabled={isLoadingMore}
                className="border-line text-content-2 hover:border-line-strong mt-3 w-full rounded-lg border py-2 text-sm disabled:opacity-50"
              >
                {isLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            ) : null}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={confirm}
        disabled={selectedIds.size === 0}
        className="bg-brand hover:bg-mint-green-200 text-content-on-brand shrink-0 rounded-full py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40"
      >
        {selectedIds.size > 0
          ? `Share ${selectedIds.size} order${selectedIds.size > 1 ? 's' : ''}`
          : 'Share Order'}
      </button>
    </Modal>
  );
}
